import { useRef, RefObject } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'

import { editPhotoActions } from '../../../reducers/editPhoto'
import { cursorStyle } from '../../../utils/cursorStyles'

interface UseDrawingEventsReturn {
    handleMouseDown: (e: any) => void
    handleMouseMove: (e: any) => void
    handleMouseUp: () => void
    showCursor: () => void
    hideCursor: () => void
    isDrawing: RefObject<boolean>
}

const MOUSE_WINDOW_DRAW_DELAY = 10
const FULL_WINDOW_DRAW_DELAY = 100

/**
 * 描画イベント（マウスダウン、マウスムーブ、マウスアップ）を管理するカスタムHook
 */
export function useDrawingEvents(stageWrapRef: RefObject<HTMLDivElement>): UseDrawingEventsReturn {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
    const isDrawing = useRef(false)

    const {
        lines,
        curHead,
        curHeadSize,
        isAIEraserMode,
        tempAIEraserLines,
    } = useSelector((state: any) => state.editPhoto)

    const handleMouseDown = (e: any) => {
        isDrawing.current = true
        const pos = e.target.getStage().getPointerPosition()

        if (isAIEraserMode) {
            // AI消しゴムモードの場合、一時的な線として追加（既存の線は保持）
            dispatch(
                editPhotoActions.setTempAIEraserLines([
                    ...tempAIEraserLines,
                    {
                        head: curHead,
                        headSize: curHeadSize,
                        points: [pos.x, pos.y, pos.x, pos.y],
                        isAIEraser: true,
                    },
                ])
            )
            // AI消しゴムモードは全体を再描画
            setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), MOUSE_WINDOW_DRAW_DELAY)
        } else {
            // 通常の消しゴムモード
            const newLines = [
                ...lines,
                {
                    head: curHead,
                    headSize: curHeadSize,
                    points: [pos.x, pos.y, pos.x, pos.y],
                },
            ]
            dispatch(editPhotoActions.setLines(newLines))
            // 通常モードはマウス位置周辺のみ描画（パフォーマンス向上）
            setTimeout(
                () => dispatch(editPhotoActions.drawPreviewImage({ mouseX: pos.x, mouseY: pos.y })),
                MOUSE_WINDOW_DRAW_DELAY
            )
        }
    }

    const handleMouseMove = (e: any) => {
        // 描画中でなければスキップ
        if (!isDrawing.current) {
            return
        }

        const stage = e.target.getStage()
        const point = stage.getPointerPosition()

        if (isAIEraserMode) {
            // AI消しゴムモードの場合、一時的な線にポイントを追加
            const lastLine = tempAIEraserLines[tempAIEraserLines.length - 1]
            if (lastLine) {
                const updatedLine = { ...lastLine, points: [...lastLine.points, point.x, point.y] }
                // 最後の線を更新、他の線は保持
                const newLines = [...tempAIEraserLines.slice(0, -1), updatedLine]
                dispatch(editPhotoActions.setTempAIEraserLines(newLines))
            }
        } else {
            // 通常の消しゴムモード
            dispatch(editPhotoActions.addPoinToLastLine({ x: point.x, y: point.y }))
        }

        // AI消しゴムモードの場合は全体を再描画、通常モードは部分描画
        if (isAIEraserMode) {
            setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), MOUSE_WINDOW_DRAW_DELAY)
        } else {
            setTimeout(
                () => dispatch(editPhotoActions.drawPreviewImage({ mouseX: point.x, mouseY: point.y })),
                MOUSE_WINDOW_DRAW_DELAY
            )
        }
    }

    const handleMouseUp = () => {
        isDrawing.current = false

        if (isAIEraserMode && tempAIEraserLines.length > 0) {
            // AI消しゴムモードの場合、一時的な線を確定済みの線に移動
            dispatch(editPhotoActions.applyAIEraserLines())
            // 履歴に保存
            setTimeout(() => {
                dispatch(editPhotoActions.pushHistoryIfChanged())
                dispatch(editPhotoActions.drawPreviewImage({}))
            }, FULL_WINDOW_DRAW_DELAY)
        } else if (!isAIEraserMode && lines.length > 0) {
            // 通常の消しゴムモード
            // 描画完了後に現在の状態を履歴に保存（ただし重複を避ける）
            setTimeout(() => {
                dispatch(editPhotoActions.pushHistoryIfChanged())
                dispatch(editPhotoActions.drawPreviewImage({}))
            }, FULL_WINDOW_DRAW_DELAY)
        }
    }

    const showCursor = () => {
        if (stageWrapRef.current) {
            stageWrapRef.current.style.cursor = cursorStyle(curHead, curHeadSize)
        }
    }

    const hideCursor = () => {
        if (stageWrapRef.current) {
            stageWrapRef.current.style.cursor = 'default'
        }
    }

    return {
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        showCursor,
        hideCursor,
        isDrawing,
    }
}
